import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import DataTable from "components/table";
import Button from "components/button";
import Switch from "components/switch";
import { useDispatch, useSelector } from "react-redux";
import { getAnnouncements, updateAnnouncement } from "store/announcementSlice";
import EditAnnouncementModal from "./EditAnnouncementModal";
import DeleteAnnouncementModal from "./DeleteAnnouncementModal";
import AddAnnouncement from "./AddAnnouncement";

const columnHelper = createColumnHelper();

export default function AnnouncementTable() {
  const dispatch = useDispatch();
  const { data, error, loading } = useSelector((state) => state.announcements);
  const { announcement: announcementParms = {} } = useSelector(
    (state) => state.auth.permissions || {}
  );

  const [refreshTrigger, setRefreshTrigger] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);

  const refreshList = () => {
    setRefreshTrigger((prev) => !prev);
  };

  const handleEdit = (row) => {
    setSelectedAnnouncement(row);
    setIsEditModalOpen(true);
  };

  const handleDelete = (row) => {
    setSelectedAnnouncement(row);
    setIsDeleteModalOpen(true);
  };

  const handleStatusToggle = useCallback(
    async (announcement) => {
      const newIsActive = !announcement.isActive;
      try {
        const result = await dispatch(
          updateAnnouncement({ _id: announcement._id, isActive: newIsActive })
        ).unwrap();
        if (result?.statuscode === 200 || result?.statuscode === 201) {
          refreshList();
        }
      } catch (err) {
        console.error("Toggle status failed:", err);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    const page = pagination.pageIndex + 1;
    const limit = pagination.pageSize;
    dispatch(getAnnouncements({ page, limit, search: searchText }));
  }, [
    dispatch,
    pagination.pageIndex,
    pagination.pageSize,
    searchText,
    refreshTrigger,
  ]);

  const totalPages = useMemo(() => {
    const totalCount = data?.total ?? 0;
    const pageSize = pagination.pageSize;
    if (!pageSize || pageSize <= 0) return 0;
    return Math.ceil(totalCount / pageSize);
  }, [data?.total, pagination.pageSize]);

  const announcementList = useMemo(() => {
    if (Array.isArray(data)) return data;
    return data?.data ?? data?.announcements ?? [];
  }, [data]);
  function formatDate(dateStr) {
    const date = new Date(dateStr);

    const options = {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };

    const formatted = new Intl.DateTimeFormat("en-GB", options).format(date);

    // Replace comma if exists
    return formatted.replace(",", "");
  }

  const columns = React.useMemo(
    () => [
      columnHelper.accessor("message", {
        id: "message",
        header: "MESSAGE",
        enableHiding: false,
        cell: (info) => {
          const value = info.getValue();
          if (!value) return "-";
          return value.length > 60 ? `${value.slice(0, 60)}...` : value;
        },
      }),
      columnHelper.accessor("backgroundColor", {
        id: "backgroundColor",
        header: "BG COLOR",
        cell: (info) => {
          const hex = info.getValue();
          if (!hex) return "-";
          return (
            <span className="flex items-center gap-2">
              <span
                className="h-5 w-5 shrink-0 rounded border border-gray-300"
                style={{ backgroundColor: hex }}
                title={hex}
              />
              <span className="text-xs text-navy-700 dark:text-white">
                {hex}
              </span>
            </span>
          );
        },
      }),
      columnHelper.accessor("textColor", {
        id: "textColor",
        header: "TEXT COLOR",
        cell: (info) => {
          const hex = info.getValue();
          if (!hex) return "-";
          return (
            <span className="flex items-center gap-2">
              <span
                className="h-5 w-5 shrink-0 rounded border border-gray-300"
                style={{ backgroundColor: hex }}
                title={hex}
              />
              <span className="text-xs text-navy-700 dark:text-white">
                {hex}
              </span>
            </span>
          );
        },
      }),
      columnHelper.accessor("isActive", {
        id: "isActive",
        header: "STATUS",
        cell: (info) => {
          const announcement = info.row.original;
          const active = info.getValue();
          return (
            <div className="flex items-center gap-2">
              <Switch
                checked={!!active}
                onChange={() => handleStatusToggle(announcement)}
              />
              <span className="text-xs text-navy-700 dark:text-white">
                {active ? "Active" : "Inactive"}
              </span>
            </div>
          );
        },
      }),
      columnHelper.accessor("createdAt", {
        id: "createdAt",
        header: "CREATED AT",
        cell: (info) => formatDate(info.getValue()),
      }),
      columnHelper.accessor("updatedAt", {
        id: "updatedAt",
        header: "UPDATED AT",
        cell: (info) => formatDate(info.getValue()),
      }),
      columnHelper.display({
        id: "action",
        header: "ACTIONS",
        enableHiding: false,
        cell: ({ row }) => {
          const announcement = row.original;
          return (
            <div className="flex gap-1">
              {announcementParms?.edit && (
                <Button onClick={() => handleEdit(announcement)}>Edit</Button>
              )}
              {announcementParms?.delete && (
                <Button
                  variant="red"
                  onClick={() => handleDelete(announcement)}
                >
                  Delete
                </Button>
              )}
            </div>
          );
        },
      }),
    ],
    [handleStatusToggle]
  );

  const initialColumnVisibility = {
    message: true,
    backgroundColor: true,
    textColor: true,
    isActive: true,
    createdAt: true,
    updatedAt: false,
    action: true,
  };

  return (
    <>
      {isEditModalOpen && selectedAnnouncement && (
        <EditAnnouncementModal
          announcement={selectedAnnouncement}
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedAnnouncement(null);
          }}
          refreshList={refreshList}
        />
      )}
      {isDeleteModalOpen && selectedAnnouncement && (
        <DeleteAnnouncementModal
          announcement={selectedAnnouncement}
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setSelectedAnnouncement(null);
          }}
          refreshList={refreshList}
        />
      )}
      <DataTable
        title="Announcements"
        columns={columns}
        data={announcementList}
        pageSize={10}
        error={error}
        loading={loading}
        totalPages={totalPages}
        total={data?.total}
        pagination={pagination}
        setPagination={setPagination}
        isColumnDropdown={true}
        initialColumnVisibility={initialColumnVisibility}
        searchText={searchText}
        setSearchText={setSearchText}
        searchPlaceholder="Search Announcement"
        rightAccessory={
          announcementParms?.create && (
            <AddAnnouncement refreshList={refreshList} />
          )
        }
      />
    </>
  );
}
